import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { getVisitation, selectVisitation, selectVisitationState } from '../../store/entities/visitation';
import { Input } from '../common/form/Input';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { ShowResourceContent } from '../ShowResourceContent';
import { Box, Paper } from '@mui/material';
import { FaIcon } from '../common/FaIcon';
import { QcsTooltip } from '../common/basic/QcsTooltip';
import { QcsTypography } from '../common/basic/QcsTypography';
import { ReadOnlyField } from '../common/basic/ReadOnlyField';
import { getLangNameObj, getLanguageVariantName } from '../../utils/format';
import { formatDate } from '../../utils/date';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { useNavigation } from '../../utils/urlPaths';
import { SubmitAndNewButtons } from '../common/form/SubmitAndNewButtons';
import { visitationListActions } from '../../store/entities/visitationList';
import { InvitationType } from '@api/models/enums/InvitationType';
import { supplierCarService, visitationService } from '@api/services/services';
import { VisitationDetailDto } from '@api/models/visitations/VisitationDetailDto';
import { UserRole } from '@api/models/enums/UserRole';
import { ReferenceDto } from '@api/models/employees/ReferenceDto';
import { linkSx, sectionLabelSx } from '../invitation/Detail/styles';
import { VisitationEntryRelease } from './VisitationEntryRelease';
import { resources } from '@api/resources';

interface LinkableValueProps {
  value?: string;
  tooltip?: string;
  link?: string;
  linkRoles?: UserRole[];
  visitationId?: string;
}

const LinkableValue: FC<LinkableValueProps> = ({ value, tooltip, link, linkRoles, visitationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const identity = useAppSelector(selectIdentity);
  const linkIsInvalid = !link || link.split('/').some((part) => part === 'undefined');
  const showLink = !linkIsInvalid && hasRole(identity.roles, linkRoles ?? []);

  const handleClick = () => {
    navigate(link!, { state: { back: `/visitation/${visitationId}` } });
  };

  const row = (
    <Box
      onClick={showLink ? handleClick : undefined}
      sx={[{ display: 'flex', alignItems: 'center', gap: 0.75, width: 'fit-content' }, ...(showLink ? [linkSx] : [])]}
    >
      <QcsTypography variant="body2">{value || '—'}</QcsTypography>
      {showLink && <FaIcon icon={faUpRightFromSquare} width={11} height={11} style={{ opacity: 0.4, flexShrink: 0 }} />}
    </Box>
  );

  return showLink && tooltip ? (
    <QcsTooltip title={t(tooltip)} placement="top">
      {row}
    </QcsTooltip>
  ) : (
    row
  );
};

interface InfoRowProps extends LinkableValueProps {
  label: string;
}

const InfoRow: FC<InfoRowProps> = ({ label, ...linkableProps }) => {
  const { t } = useTranslation();
  return (
    <ReadOnlyField label={t(label)}>
      <LinkableValue {...linkableProps} />
    </ReadOnlyField>
  );
};

interface InfoListRowProps {
  label: string;
  items?: ReferenceDto[];
  tooltip?: string;
  linkBuilder: (item: ReferenceDto) => string;
  linkRoles?: UserRole[];
  visitationId?: string;
}

const InfoListRow: FC<InfoListRowProps> = ({ label, items, tooltip, linkBuilder, linkRoles, visitationId }) => {
  const { t, i18n } = useTranslation();

  if (!items || items.length === 0) {
    return (
      <ReadOnlyField label={t(label)} valueSx={{ fontWeight: 500 }}>
        —
      </ReadOnlyField>
    );
  }

  return (
    <ReadOnlyField label={t(label)}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.map((item, index) => (
          <LinkableValue
            key={item.id ?? index}
            value={getLangNameObj(i18n, item)}
            tooltip={tooltip}
            link={linkBuilder(item)}
            linkRoles={linkRoles}
            visitationId={visitationId}
          />
        ))}
      </Box>
    </ReadOnlyField>
  );
};

interface VisitationDetailProps {
  visitationId?: string;
  embedded?: boolean;
}

export const VisitationDetail: FC<VisitationDetailProps> = ({ visitationId: visitationIdProp, embedded }) => {
  const { t, i18n } = useTranslation();
  const { getBackPath: backTo } = useNavigation('/visitation');
  const params = useParams();
  const visitationId = visitationIdProp ?? params.visitationId;
  const visitation = useAppSelector(selectVisitation);
  const identity = useAppSelector(selectIdentity);
  const dispatch = useAppDispatch();
  const [carType, setCarType] = useState<string>('');
  const [carId, setCarId] = useState<string>();

  const isOneTime =
    visitation.visitation?.invitation?.invitationType === InvitationType.OneTime ||
    visitation.visitation?.invitation?.invitationType === InvitationType.OneTimeEntry;
  const linkEmployee = isOneTime ? 'visit' : 'supplier';
  const linkInvitation = isOneTime ? 'person' : '';

  useEffect(() => {
    dispatch(getVisitation(visitationId!));
  }, [visitationId, dispatch]);

  useEffect(() => {
    const fetchCarType = async () => {
      const spz = visitation.visitation?.carSpz;
      const supplierId = visitation.visitation?.supplier?.id;
      if (!spz || !supplierId) return;

      const response = await supplierCarService.getSupplierCars({ spz, supplierId });
      const car = response.data.content?.find((c) => c.spz === spz && c.supplier?.id === supplierId);
      setCarId(car?.id);
      if (car?.type?.languageVariants) setCarType(getLanguageVariantName(car.type.languageVariants, i18n.language));
    };

    if (visitation.visitation) fetchCarType();
  }, [visitation.visitation, i18n]);

  const handleSubmit = async (data: VisitationDetailDto) => {
    await visitationService.updateVisitation(visitationId!, data);
    dispatch(getVisitation(visitationId!));
    dispatch(visitationListActions.reload(true));
  };

  const supplierId = visitation.visitation?.supplier?.id;
  const canEditNote = hasRole(identity.roles, [
    UserRole.AdminQcs,
    UserRole.ManagerOsah,
    UserRole.ManagerWorkplace,
    UserRole.Receptionist,
    UserRole.ExternalManager,
    UserRole.ExternalWorker,
  ]);
  const employeeLinkRoles = [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist];
  const facilityLinkRoles = [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah];
  const activityLinkRoles = [UserRole.AdminQcs, UserRole.ManagerOsah];
  const internalEmployeeLinkRoles = [UserRole.AdminQcs, UserRole.AdminCompany, UserRole.ManagerOsah];

  return (
    <ShowResourceContent resource={resources.getVisitation} fetchStateSelector={selectVisitationState}>
      {!embedded && <DetailHeader title={getLangNameObj(i18n, visitation.visitation?.invitation)} backTo={backTo} />}
      <FormikDebounce<VisitationDetailDto> initialValues={{ ...visitation }} onSubmit={handleSubmit}>
        <Form>
          <FormContainer style={{ maxWidth: '100%' }}>
            {visitation.entryReleased && (
              <VisitationEntryRelease entryReleased={visitation.entryReleased} unmetConditions={visitation.unmetConditions} />
            )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
              <QcsTypography variant="body2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
                {t('visitation.visitDetail.title')}
              </QcsTypography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', columnGap: 3, rowGap: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <InfoRow
                    label="visitation.invitationPerson"
                    value={visitation.visitation?.visitor?.name}
                    tooltip="visitation.tooltips.linkToSupplierEmployee"
                    link={`/${linkEmployee}/${supplierId}/employee/${visitation.visitation?.visitor?.id}`}
                    linkRoles={employeeLinkRoles}
                    visitationId={visitationId}
                  />
                  <InfoRow
                    label="visitation.establishment"
                    value={getLangNameObj(i18n, visitation.visitation?.establishment)}
                    tooltip="visitation.tooltips.linkToEstablishment"
                    link={`/establishment/${visitation.visitation?.establishment?.id}`}
                    linkRoles={facilityLinkRoles}
                    visitationId={visitationId}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', columnGap: 3, rowGap: 2 }}>
                    <Box>
                      <ReadOnlyField label={t('visitation.entryTime')} valueSx={{ fontWeight: 500 }}>
                        {formatDate(i18n, visitation.visitation?.entryTime, true, true) || '—'}
                      </ReadOnlyField>
                    </Box>
                    <Box>
                      <InfoRow
                        label="visitation.registeredPersonEntrance"
                        value={visitation.entryUser?.name}
                        tooltip="visitation.tooltips.linkToEmployee"
                        link={`/employee/${visitation.entryUser?.id}`}
                        linkRoles={internalEmployeeLinkRoles}
                        visitationId={visitationId}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <InfoRow
                    label="visitation.supplier"
                    value={visitation.visitation?.supplier?.name}
                    tooltip="visitation.tooltips.linkToSupplier"
                    link={`/${linkEmployee}/${supplierId}` + (isOneTime ? `/employee/${visitation.visitation?.visitor?.id}` : '')}
                    linkRoles={employeeLinkRoles}
                    visitationId={visitationId}
                  />
                  <InfoRow
                    label="visitation.invitedBy"
                    value={visitation.visitation?.invitedBy?.name}
                    tooltip="visitation.tooltips.linkToEmployee"
                    link={`/employee/${visitation.visitation?.invitedBy?.id}`}
                    linkRoles={internalEmployeeLinkRoles}
                    visitationId={visitationId}
                  />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', columnGap: 3, rowGap: 2 }}>
                    <Box>
                      <ReadOnlyField label={t('visitation.exitTime')} valueSx={{ fontWeight: 500 }}>
                        {formatDate(i18n, visitation.visitation?.exitTime, true, true) || '—'}
                      </ReadOnlyField>
                    </Box>
                    <Box>
                      <InfoRow
                        label="visitation.registeredPersonExit"
                        value={visitation.exitUser?.name}
                        tooltip="visitation.tooltips.linkToEmployee"
                        link={`/employee/${visitation.exitUser?.id}`}
                        linkRoles={internalEmployeeLinkRoles}
                        visitationId={visitationId}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {(visitation.visitation?.carSpz || carType) && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
                <QcsTypography variant="body2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
                  {t('supplier.car.detailTitle')}
                </QcsTypography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 3, rowGap: 2 }}>
                  <Box>
                    <InfoRow
                      label="supplier.car.spz"
                      value={visitation.visitation?.carSpz}
                      tooltip="visitation.tooltips.linkToCar"
                      link={`/supplier/${supplierId}/cars/${carId}`}
                      linkRoles={employeeLinkRoles}
                      visitationId={visitationId}
                    />
                  </Box>
                  <Box>
                    <ReadOnlyField label={t('supplier.car.type')} valueSx={{ fontWeight: 500 }}>
                      {carType || '—'}
                    </ReadOnlyField>
                  </Box>
                </Box>
              </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider' }}>
              <QcsTypography variant="body2" sx={{ ...sectionLabelSx, mb: 1.5 }}>
                {t('visitation.invitationDetail.title')}
              </QcsTypography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, columnGap: 3, rowGap: 2 }}>
                <Box>
                  <InfoRow
                    label="visitation.invitation"
                    value={visitation.visitation?.invitation?.name}
                    tooltip="visitation.tooltips.linkToInvitation"
                    link={`/invitation/${visitation.visitation?.invitation?.id}/${linkInvitation}`}
                    linkRoles={[
                      UserRole.AdminQcs,
                      UserRole.AdminCompany,
                      UserRole.ManagerOsah,
                      UserRole.ManagerWorkplace,
                      UserRole.Receptionist,
                      UserRole.ExternalManager,
                      UserRole.ExternalWorker,
                      UserRole.ExternalOneTime,
                    ]}
                    visitationId={visitationId}
                  />
                </Box>
                <Box>
                  <InfoRow
                    label="visitation.invitationDetail.responsibleSupplierPerson"
                    value={visitation.visitation?.supplierResponsiblePerson?.name}
                    tooltip="visitation.tooltips.linkToSupplierEmployee"
                    link={`/${linkEmployee}/${supplierId}/employee/${visitation.visitation?.supplierResponsiblePerson?.id}`}
                    linkRoles={employeeLinkRoles}
                    visitationId={visitationId}
                  />
                </Box>
                <Box>
                  <InfoListRow
                    label="visitation.workplace"
                    items={visitation.visitation?.workplaces}
                    tooltip="visitation.tooltips.linkToWorkplace"
                    linkBuilder={(item) => `/workplace/${item.id}`}
                    linkRoles={facilityLinkRoles}
                    visitationId={visitationId}
                  />
                </Box>
                <Box>
                  <InfoListRow
                    label="invitation.activities"
                    items={visitation.visitation?.invitation?.activities}
                    tooltip="visitation.tooltips.linkToActivity"
                    linkBuilder={(item) => `/activity/${item.id}`}
                    linkRoles={activityLinkRoles}
                    visitationId={visitationId}
                  />
                </Box>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'primary.light' }}>
              <Input name="visitation.note" label={t('common.notes')} multiline rows={3} fullWidth disabled={!canEditNote} />
              {canEditNote && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.5 }}>
                  <SubmitAndNewButtons id={visitationId} />
                </Box>
              )}
            </Paper>
          </FormContainer>
        </Form>
      </FormikDebounce>
    </ShowResourceContent>
  );
};
